/* eslint-disable */
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useContext,
  useMemo,
} from "react";
import PropTypes from "prop-types";

import { client } from "../../services/axiosClient";
import "moment/locale/es";
import "moment/locale/pt";
import cx from "classnames";
import { Slide as ToastSlide, toast } from "react-toastify";
import { useImmer } from "use-immer";
import { useTranslation } from "react-i18next";

import moment, { isMoment } from "moment/moment";
import PerfectScrollbar from "perfect-scrollbar";

import { DragDropContext } from "react-beautiful-dnd";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";

import Collapse from "@material-ui/core/Collapse";

// @material-ui/icons
import Icon from "@material-ui/core/Icon";
import { AddCircle, AttachMoney, FilterList } from "@material-ui/icons";
import { ArrowBack } from "@material-ui/icons";

// core components
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import Button from "components/CustomButtons/Button";

import ActionBar from "components/ActionBar/ActionBar";
import ActionBarButton from "components/ActionBar/ActionBarButton";
import ActionDropdown from "components/ActionDropdown/ActionDropdown";
import FilterTagInitiatives from "components/FilterTag/FilterTagInitiatives";
import FullPageLoader from "components/Loaders/FullPageLoader";
import MultiLineChart from "../../components/Charts/MultiLineChart";

import styles from "../../assets/jss/material-dashboard-pro-react/views/initiativesStyle";
import {
  blendColors,
  grayColor,
} from "../../assets/jss/material-dashboard-pro-react";

import FilterDialog from "components/FilterDialog/FilterDialog";
import { deepEqual } from "../../utils/utils";
import { generateContrastingColors } from "../../utils/colorUtils";
import useThemeColors from "../../hooks/useThemeColors";
import AuthContext from "../../contexts/AuthProvider";
import DroppableStageColumn from "../../components/Initiatives/DroppableStageColumn";
import InitiativeDetailsCard from "../../components/Initiatives/InitiativeDetailsCard";
import InitiativeExecutionCard from "../../components/Initiatives/InitiativeExecutionCard";
import NewFilterDialog from "../../components/Initiatives/NewFilterDialog";
import TagManagementDialog from "../../components/Initiatives/TagManagementDialog";
import ConsolidatedInitiativesGantt from "../../components/Initiatives/ConsolidatedInitiativesGantt";
import AddUpdateDialog from "../../components/Initiatives/AddUpdateDialog";
import InitiativesStatsCards from "../../components/Initiatives/InitiativesStatsCards";
import DeleteInitiativeDialog from "../../components/Dialog/DeleteInitiativeDialog";

const benefitDecimals = 2;

const getInitiativeDisplayStatus = (initiative) => {
  if (!initiative) return "gray"; // Safety check

  const { status, follow_up } = initiative;

  // Terminal states have priority
  if (status === "finished") return "green";
  if (status === "on_hold") return "yellow";
  if (status === "cancelled") return "red";

  // For 'active' status, the traffic light determines the color
  if (status === "active" && follow_up && follow_up.length > 0) {
    const trafficLight = follow_up[0].traffic_light;
    if (trafficLight === 1) return "green";
    if (trafficLight === 2) return "yellow";
    if (trafficLight === 3) return "red";
  }

  // Fallback for any other case (e.g., new, no status)
  return "gray";
};

export default function Initiatives() {
  const { clientColor, clientTextColor } = useThemeColors();
  const { userDetails } = useContext(AuthContext);
  const useStyles = makeStyles(styles);
  const classes = useStyles();

  // Use the extracted color utility function
  const generateColors = useCallback((baseColor, count = 6) => {
    return generateContrastingColors(baseColor, count);
  }, []);

  const { t, i18n } = useTranslation();
  const [screenBusy, setScreenBusy] = useState(true);
  const [initiativesBusy, setInitiativesBusy] = useState(false);

  const initLongDescriptionRef = useRef(null);
  const [addUpdateModalOpen, setAddUpdateModalOpen] = useState(false);
  const [initiatives, setInitiatives] = useImmer({});
  const [initiativesSortedBy, setInitiativesSortedBy] =
    useState("benefit_global");
  const [initiativesFilters] = useImmer({
    active: true,
    on_hold: false,
    cancelled: false,
    finished: false,
    status1: true,
    status2: true,
    status3: true,
    updated: true,
    outdated: true,
  });
  const [selectedInitiative, setSelectedInitiative] = useState(null);

  // Consolidated initiative form state
  const [initiativeForm, setInitiativeForm] = useImmer({
    // Form data
    shortDescription: "",
    shortDescriptionPrinted: null,
    leader: "",
    benefit: "0.0",
    benefitGlobal: "0.0",
    scopeMarginRatio: "1.0",
    benefitStart: null,
    benefitEnd: null,
    projectStart: null,
    longDescription: "",
    stage: "opportunity",
    tags: [],
    pnlImpactLine: null,
    scope: [],

    // Validation status
    shortDescriptionStatus: false,
    leaderStatus: false,
    benefitStatus: false,
    benefitStartStatus: false,
    benefitEndStatus: false,
    projectStartStatus: false,
  });

  const shortDescriptionInput = useRef(null);
  const initiativesWrapper = useRef(null);

  // Ref and state for dynamic height matching
  const detailsPanelRef = useRef(null);
  const [detailsPanelHeight, setDetailsPanelHeight] = useState(null);
  const [scopeChanged, setScopeChanged] = useState(false);
  const [metaLoaded, setMetaLoaded] = useState(false);
  const [commonFilters, setCommonFilters] = useImmer({});
  const [commonFiltersSequence, setCommonFiltersSequence] = useState([]);
  const [commonFiltersMode, setCommonFiltersMode] = useImmer({});
  const [commonFiltersIndexes, setCommonFiltersIndexes] = useImmer({});
  const [filterModalCurrentFilter, setFilterModalCurrentFilter] = useState("");
  const [filterModalFilterName, setFilterModalFilterName] = useState("");
  const [filterModalItemsChoices, setFilterModalItemsChoices] = useState([]);
  const [filterModalFilterMode, setFilterModalFilterMode] = useState("");
  const [filterModalItemsSelected, setFilterModalItemsSelected] = useState([]);
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const [layoutMode, setLayoutMode] = useState("expanded"); // "expanded" or "collapsed"
  const [initiativesView, setInitiativesView] = useState("table"); // "table", "board", or "gantt"
  const [viewMode, setViewMode] = useState("month"); // Added for monthly/quarterly/yearly buttons
  const [loadingInitiativeId, setLoadingInitiativeId] = useState(null);
  const [collapsedStages, setCollapsedStages] = useState(new Set());
  const [tags, setTags] = useState([]);
  const [tagManagementModalOpen, setTagManagementModalOpen] = useState(false);
  const [editingTag, setEditingTag] = useState(null); // To hold the tag being edited {id, name}
  const [newTagName, setNewTagName] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [pnlStructure, setPnlStructure] = useState([]); // Will hold the P&L structure from the API

  // Chart names from implementation
  const [chartNames, setChartNames] = useState({
    sales_chart_name: "Monthly Sales",
    serve_margin_chart_name: "Serve Margin",
  });

  // State to control chart overlay when data is being updated
  const [showChartOverlay, setShowChartOverlay] = useState(false);
  const [overlayInitiativeIds, setOverlayInitiativeIds] = useState(new Set());

  // State to force re-render when filters change
  const [filterUpdateTimestamp, setFilterUpdateTimestamp] = useState(
    Date.now(),
  );

  // Currency-related state
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [currencyOptionsAvailable, setCurrencyOptionsAvailable] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState(null);

  // Helper functions for initiative form state management
  const updateInitiativeForm = (updates) => {
    setInitiativeForm((draft) => {
      Object.assign(draft, updates);
    });
  };

  const setInitiativeFormValue = (key, value) => {
    setInitiativeForm((draft) => {
      // Handle date validation logic
      if (key === "projectStart") {
        if (isMoment(value)) {
          draft[key] = value;

          // If project start moves to a date after capture start, move capture dates together
          if (
            isMoment(draft.benefitStart) &&
            value.isAfter(draft.benefitStart)
          ) {
            draft.benefitStart = value.clone();
            // Also move capture full potential to maintain consistency
            if (isMoment(draft.benefitEnd)) {
              draft.benefitEnd = value.clone();
            }
          }
        } else {
          draft[key] = value;
        }
      } else if (key === "benefitStart" || key === "benefitEnd") {
        // For capture start and capture full potential dates
        draft[key] = value;

        // If both dates are filled and one changes, ensure consistency
        if (isMoment(draft.benefitStart) && isMoment(draft.benefitEnd)) {
          if (draft.benefitEnd.isBefore(draft.benefitStart)) {
            // If capture full potential is before capture start, equalize them
            draft.benefitEnd = draft.benefitStart.clone();
          }
        }
      } else {
        draft[key] = value;
      }
    });
  };

  // Helper function to set all form statuses at once
  const setAllFormStatuses = (isValid) => {
    const statusFields = [
      "shortDescriptionStatus",
      "leaderStatus",
      "benefitStatus",
      "projectStartStatus",
      "benefitStartStatus",
      "benefitEndStatus",
    ];

    statusFields.forEach((field) => {
      setInitiativeFormValue(field, isValid);
    });
  };

  //const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useImmer({
    statuses: ["active", "on_hold", "cancelled", "finished"],
    project_start_after: null,
    capture_end_before: null,
    tag_ids: [],
  });
  const [tempFilters, setTempFilters] = useImmer(activeFilters);
  const [confirmDeleteTag, setConfirmDeleteTag] = useState(null);

  // New comprehensive filter state
  const [newFilterModalOpen, setNewFilterModalOpen] = useState(false);
  const [deleteInitiativeModalOpen, setDeleteInitiativeModalOpen] =
    useState(false);
  const [isDeletingInitiative, setIsDeletingInitiative] = useState(false);
  const [newFilters, setNewFilters] = useImmer({
    stage: null,
    leader: null,
    tags: null,
  });
  const [tempNewFilters, setTempNewFilters] = useImmer({
    stage: null,
    leader: null,
    tags: null,
  });
  const [tagActionLoading, setTagActionLoading] = useState(false);

  // Define stages
  const stages = [
    "opportunity",
    "in_planning",
    "in_implementation",
    "in_capture",
    "concluded",
  ];

  // Define status order for sorting within stages
  const statusOrder = {
    red: 7, // highest priority
    yellow: 6,
    green: 5,
    active: 4,
    on_hold: 3,
    cancelled: 2,
    finished: 1, // lowest priority
  };

  const statusToColor = useCallback((status) => {
    const colorMap = {
      green: "#4CAF50",
      yellow: "#FF9800",
      red: "#F44336",
    };
    return colorMap[status] || grayColor[3];
  }, []);

  const makeStageBuckets = useCallback(
    (list) => {
      const buckets = {};
      stages.forEach((s) => (buckets[s] = []));
      list.forEach((it) => buckets[it.stage].push(it));

      // Sort initiatives within each stage by status order
      Object.keys(buckets).forEach((stage) => {
        buckets[stage].sort((a, b) => {
          const aStatus = statusOrder[a.displayStatus] || 0;
          const bStatus = statusOrder[b.displayStatus] || 0;
          return bStatus - aStatus; // Higher status values first (red, yellow, green, active, etc.)
        });
      });

      return buckets;
    },
    [stages, statusOrder],
  );

  const toggleLayout = useCallback(() => {
    const newMode = layoutMode === "expanded" ? "collapsed" : "expanded";
    setLayoutMode(newMode);

    // Clear selection when expanding back to full view
    if (newMode === "expanded") {
      setSelectedInitiative(null);
      setShowChartOverlay(false);
    }
  }, [layoutMode]);

  const toggleStage = useCallback(
    (
      stage, // NEW
    ) =>
      setCollapsedStages((prev) => {
        const next = new Set(prev);
        next.has(stage) ? next.delete(stage) : next.add(stage);
        return next;
      }),
    [],
  );

  const toGanttRow = (it) => ({
    id: it.id,
    initiative: it.initiative,
    leader: it.leader,
    globalBenefit: +(it.margin_benefit_global * 100).toFixed(1),
    status: it.displayStatus,
    proj_start: it.project_start,
    cap_start: it.margin_benefit_start,
    cap_end: it.margin_benefit_end,
  });

  const [monthlySalesChartData, setMonthlySalesChartData] = useState({
    datasets: [],
    labels: [],
  });

  const [serveMarginMonthlyChartData, setServeMarginMonthlyChartData] =
    useState({
      datasets: [],
      labels: [],
    });

  const filtersNames = useRef({});
  const filtersItems = useRef({});
  const initialFiltersObjects = useRef({});
  const initialFiltersStrings = useRef({});
  const filtersList = useRef([]);

  const notify = useCallback(
    (text, type = "info", time = 3000) =>
      toast(t(text), {
        position: "top-right",
        autoClose: time,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
        theme: "light",
        type: type,
        transition: ToastSlide,
      }),
    [t],
  );

  const processMonthlySalesData = useCallback(
    (salesData) => {
      if (!salesData || !Array.isArray(salesData) || salesData.length === 0) {
        return { datasets: [], labels: [] };
      }

      // Group data by year
      const yearGroups = {};
      salesData.forEach((item) => {
        // Validate item structure
        if (!item) {
          return; // Skip this item
        }

        // Create a copy of the item to avoid modifying the original
        const itemCopy = { ...item };

        // Check if the item has the expected structure
        if (
          typeof itemCopy.month !== "number" ||
          typeof itemCopy.values !== "number"
        ) {
          // Try to find alternative property names
          const monthKey = Object.keys(itemCopy).find((key) =>
            key.toLowerCase().includes("month"),
          );
          const valueKey = Object.keys(itemCopy).find(
            (key) =>
              key.toLowerCase().includes("value") ||
              key.toLowerCase().includes("sales"),
          );

          if (monthKey && valueKey) {
            itemCopy.month = itemCopy[monthKey];
            itemCopy.values = itemCopy[valueKey];
          } else {
            return; // Skip this item
          }
        }

        const year = Math.floor(itemCopy.month / 100);
        const month = itemCopy.month % 100;

        if (!yearGroups[year]) {
          yearGroups[year] = {};
        }
        yearGroups[year][month] = itemCopy.values;
      });

      // Create datasets for each year
      const datasets = [];
      const colors = generateColors(clientColor, 6);
      let colorIndex = 0;

      Object.keys(yearGroups)
        .sort((a, b) => parseInt(b) - parseInt(a))
        .forEach((year) => {
          const yearData = yearGroups[year];
          const data = [];

          // Fill data for all 12 months
          for (let month = 1; month <= 12; month++) {
            data.push(yearData[month] || null);
          }

          datasets.push({
            label: year,
            data: data,
            borderColor: colors[colorIndex % colors.length],
            backgroundColor: colors[colorIndex % colors.length] + "20",
            borderWidth: 3,
            fill: false,
            tension: 0.2,
            pointRadius: 0,
            pointHoverRadius: 4,
            spanGaps: false, // Don't connect across null values
          });

          colorIndex++;
        });

      // Create labels for 12 months
      const labels = [];
      for (let month = 1; month <= 12; month++) {
        labels.push(t("months." + month));
      }

      return { datasets, labels };
    },
    [t],
  );

  // Function to calculate order of magnitude based on chart data values
  const calculateOrderOfMagnitude = useCallback((chartData) => {
    if (!chartData || !chartData.datasets || chartData.datasets.length === 0) {
      return "m"; // Default to millions
    }

    // Get all non-null values from all datasets
    const allValues = [];
    chartData.datasets.forEach((dataset) => {
      if (dataset.data && Array.isArray(dataset.data)) {
        dataset.data.forEach((value) => {
          if (value !== null && value !== undefined && !isNaN(value)) {
            allValues.push(Math.abs(value));
          }
        });
      }
    });

    if (allValues.length === 0) {
      return "m"; // Default to millions
    }

    // Find the maximum value to determine the order of magnitude
    const maxValue = Math.max(...allValues);

    if (maxValue >= 1e9) {
      return "b"; // Billions
    } else if (maxValue >= 1e6) {
      return "m"; // Millions
    } else if (maxValue >= 1e3) {
      return "k"; // Thousands
    } else {
      return ""; // No suffix for values less than 1000
    }
  }, []);

  const processMonthlyServeMarginData = useCallback(
    (serveMarginData) => {
      if (
        !serveMarginData ||
        !Array.isArray(serveMarginData) ||
        serveMarginData.length === 0
      ) {
        return { datasets: [], labels: [] };
      }

      // Group data by year
      const yearGroups = {};
      serveMarginData.forEach((item) => {
        // Validate item structure
        if (!item) {
          return; // Skip this item
        }

        // Create a copy of the item to avoid modifying the original
        const itemCopy = { ...item };

        // Check if the item has the expected structure
        if (
          typeof itemCopy.month !== "number" ||
          typeof itemCopy.values !== "number"
        ) {
          // Try to find alternative property names
          const monthKey = Object.keys(itemCopy).find((key) =>
            key.toLowerCase().includes("month"),
          );
          const valueKey = Object.keys(itemCopy).find(
            (key) =>
              key.toLowerCase().includes("value") ||
              key.toLowerCase().includes("margin"),
          );

          if (monthKey && valueKey) {
            itemCopy.month = itemCopy[monthKey];
            itemCopy.values = itemCopy[valueKey];
          } else {
            return; // Skip this item
          }
        }

        const year = Math.floor(itemCopy.month / 100);
        const month = itemCopy.month % 100;

        if (!yearGroups[year]) {
          yearGroups[year] = {};
        }
        // Convert float to percentage (multiply by 100)
        yearGroups[year][month] = itemCopy.values * 100;
      });

      // Create datasets for each year
      const datasets = [];
      const colors = generateColors(clientColor, 6);
      let colorIndex = 0;

      Object.keys(yearGroups)
        .sort((a, b) => parseInt(b) - parseInt(a))
        .forEach((year) => {
          const yearData = yearGroups[year];
          const data = [];

          // Fill data for all 12 months
          for (let month = 1; month <= 12; month++) {
            data.push(yearData[month] || null);
          }

          datasets.push({
            label: year,
            data: data,
            borderColor: colors[colorIndex % colors.length],
            backgroundColor: colors[colorIndex % colors.length] + "20",
            borderWidth: 3,
            fill: false,
            tension: 0.2,
            pointRadius: 0,
            pointHoverRadius: 4,
            spanGaps: false, // Don't connect across null values
          });

          colorIndex++;
        });

      // Create labels for 12 months
      const labels = [];
      for (let month = 1; month <= 12; month++) {
        labels.push(t("months." + month));
      }

      return { datasets, labels };
    },
    [t],
  );

  // Update monthly sales chart labels when language changes
  useEffect(() => {
    if (monthlySalesChartData.datasets.length > 0) {
      const labels = [];
      for (let month = 1; month <= 12; month++) {
        labels.push(t("months." + month));
      }
      setMonthlySalesChartData((prev) => ({
        ...prev,
        labels: labels,
      }));
    }
  }, [i18n.language, t]);

  // Update monthly serve margin chart labels when language changes
  useEffect(() => {
    if (serveMarginMonthlyChartData.datasets.length > 0) {
      const labels = [];
      for (let month = 1; month <= 12; month++) {
        labels.push(t("months." + month));
      }
      setServeMarginMonthlyChartData((prev) => ({
        ...prev,
        labels: labels,
      }));
    }
  }, [i18n.language, t]);

  useEffect(() => {
    setScreenBusy(true);
    Promise.all([
      client.post("api/action_plans_initiatives/"),
      client.get("api/initiatives_tags/"),
    ])
      .then(([initiativesRes, tagsRes]) => {
        const response = initiativesRes;
        const filters = response.data.filters;
        filtersList.current = filters && filters.list ? filters.list : [];
        filtersNames.current = filters && filters.names ? filters.names : {};
        filtersItems.current = filters && filters.items ? filters.items : {};
        let initialCommonFiltersObjects = {};
        let initialCommonFiltersStrings = {};
        Object.keys(filters.names).forEach((name) => {
          initialCommonFiltersObjects[name] = [];
          initialCommonFiltersStrings[name] = "";
        });
        initialFiltersObjects.current = initialCommonFiltersObjects;
        initialFiltersStrings.current = initialCommonFiltersStrings;
        if (response.data && response.data.pnl_lines) {
          setPnlStructure(response.data.pnl_lines);
        }

        // Extract chart names from response
        if (response.data && response.data.chart_names) {
          setChartNames(response.data.chart_names);
        }

        // Extract currency options from response
        if (response.data && response.data.currency_options) {
          setCurrencyOptions(response.data.currency_options);
        }
        if (response.data && response.data.currency_options_available) {
          setCurrencyOptionsAvailable(response.data.currency_options_available);
          // Set default currency to first available option
          if (response.data.currency_options_available.length > 0) {
            setSelectedCurrency(response.data.currency_options_available[0]);
          }
        }

        setCommonFilters((draft) => {
          Object.entries(initialCommonFiltersObjects).forEach(
            ([key, val]) => (draft[key] = val),
          );
        });
        setCommonFiltersMode((draft) => {
          Object.entries(initialCommonFiltersStrings).forEach(
            ([key, val]) => (draft[key] = val),
          );
        });
        setCommonFiltersIndexes((draft) => {
          Object.entries(initialCommonFiltersObjects).forEach(
            ([key, val]) => (draft[key] = val),
          );
        });
        setMetaLoaded(true);
        let init = {
          ...response.data.initiatives,
          ["new"]: {
            short_description: "",
            leader: "",
            margin_benefit: 0,
            margin_benefit_global: 0,
            margin_benefit_ratio: 1,
            margin_benefit_start: null,
            margin_benefit_end: null,
            long_description: "",
            status: "new",
            stage: "opportunity",
            scope: [
              {
                filters: Object.keys(filtersNames.current).reduce(
                  (acc, key) => {
                    acc[key] = [];
                    return acc;
                  },
                  {},
                ),
                filters_sequence: [],
              },
            ],
            follow_up: [
              {
                user_name: userDetails.username,
                description: "",
                traffic_light: 0,
                event_type: "creation",
                date: moment().startOf("day").format("YYYY-MM-DD HH:mm"),
              },
            ],
            serve_margin_chart: {
              months: [],
              serve_margin: [],
            },
          },
        };
        Object.keys(init).forEach((id) => (init[id].id = id));
        Object.keys(init).forEach((id) => {
          if (!init[id].stage) {
            init[id].stage = "opportunity";
          }
        });
        setInitiatives(init);
        setTags(tagsRes.data);
        setScreenBusy(false);
      })
      .catch(() => {
        setScreenBusy(false);
      });
  }, []);

  useEffect(() => {
    let ps1Instance = null;

    if (initiativesWrapper.current && navigator.platform.indexOf("Win") > -1) {
      ps1Instance = new PerfectScrollbar(initiativesWrapper.current, {
        suppressScrollX: true,
        suppressScrollY: false,
        wheelPropagation: false,
        maxScrollbarLength: 100,
        scrollingThreshold: 1000,
      });
    }

    return function cleanup() {
      if (ps1Instance) {
        ps1Instance.destroy();
      }
    };
  }, [initiatives]);

  useEffect(() => {
    moment.locale(i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    if (selectedInitiative && initiatives[selectedInitiative]) {
      const currentScope = initiatives[selectedInitiative].scope[0];
      const backendFilters = currentScope.filters;
      const backendSequence = currentScope.filters_sequence;

      // Deep compare with current selections
      const filterChanged = !deepEqual(commonFilters, backendFilters);
      const sequenceChanged = !deepEqual(
        commonFiltersSequence,
        backendSequence,
      );

      if (filterChanged || sequenceChanged) {
        setInitiativeFormValue("scope", [
          {
            filters: commonFilters,
            filters_sequence: commonFiltersSequence,
          },
        ]);
        setScopeChanged(true);
      }
    }
  }, [commonFilters, commonFiltersSequence, selectedInitiative, initiatives]);

  useEffect(() => {
    if (scopeChanged) {
      updateInitData();
      setScopeChanged(false);
    }
  }, [scopeChanged]);

  // Clear overlay initiatives list when screen is reloaded
  useEffect(() => {
    if (!screenBusy) {
      setOverlayInitiativeIds(new Set());
    }
  }, [screenBusy]);

  // ResizeObserver to track Panel 2 height changes
  useEffect(() => {
    if (!detailsPanelRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height;
        setDetailsPanelHeight(height);
      }
    });

    resizeObserver.observe(detailsPanelRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    let benefitGlobal =
      (initiativeForm.benefit * initiativeForm.scopeMarginRatio) / 100;
    setInitiativeFormValue(
      "benefitGlobal",
      benefitGlobal.toFixed(benefitDecimals),
    );
  }, [initiativeForm.benefit, initiativeForm.scopeMarginRatio]);

  const updateInitData = useCallback(() => {
    if (initiativesBusy) return;
    setInitiativesBusy(true);

    // Always show chart overlay when updating scope and track which initiative has overlay
    setShowChartOverlay(true);
    setOverlayInitiativeIds((prev) => new Set([...prev, selectedInitiative]));

    // Check if there are any active filters (excluding period filter)
    const hasActiveFilters = Object.keys(commonFilters).some(
      (filter) =>
        filter !== "period" &&
        commonFilters[filter] &&
        commonFilters[filter].length > 0,
    );

    if (!hasActiveFilters) {
      // No scope filters selected - set ratio to 100% (1.0) without API call
      const newRatio = "100.00";
      if (newRatio !== initiativeForm.scopeMarginRatio) {
        setInitiativeFormValue("scopeMarginRatio", newRatio);
      }
      setInitiativesBusy(false);
      return;
    }

    // Make API call only when there are active filters
    client
      .post("api/initiatives_update_scope_ratio/", {
        margin_benefit: parseFloat(initiativeForm.benefit) / 100,
        filters: commonFilters,
        filters_sequence: commonFiltersSequence,
      })
      .then((response) => {
        if (response.data?.margin_benefit_ratio != null) {
          const newRatio = (100 * response.data.margin_benefit_ratio).toFixed(
            benefitDecimals,
          );
          if (newRatio !== initiativeForm.scopeMarginRatio) {
            setInitiativeFormValue("scopeMarginRatio", newRatio);
          }
        }
        // Process serve margin chart data
        setInitiativesBusy(false);
      })
      .catch(() => {
        setInitiativesBusy(false);
      });
  }, [
    initiativeForm.benefit,
    commonFilters,
    commonFiltersSequence,
    initiativesBusy,
  ]);

  const updateInitiativeData = useCallback(
    (initiative) => {
      updateInitiativeForm({
        shortDescription: initiative.short_description,
        shortDescriptionPrinted: initiative.short_description,
        leader: initiative.leader,
        benefit: (100 * initiative.margin_benefit).toFixed(benefitDecimals),
        benefitGlobal: (100 * initiative.margin_benefit_global).toFixed(
          benefitDecimals,
        ),
        scopeMarginRatio: (100 * initiative.margin_benefit_ratio).toFixed(
          benefitDecimals,
        ),
        benefitStart: initiative.margin_benefit_start
          ? moment(initiative.margin_benefit_start, "YYYY-MM")
          : null,
        benefitEnd: initiative.margin_benefit_end
          ? moment(initiative.margin_benefit_end, "YYYY-MM")
          : null,
        projectStart: initiative.project_start
          ? moment(initiative.project_start, "YYYY-MM")
          : null,
        longDescription: initiative.long_description,
        stage: initiative.stage || "opportunity",
        pnlImpactLine: initiative.pnl_impact_line || null,
        scope: initiative.scope,
        tags: initiative.tags ? initiative.tags.map((t) => t.id) : [],
        chart_data: initiative.chart_data,
      });

      const scope = initiative.scope[0];

      setCommonFilters((draft) => {
        Object.keys(draft).forEach((key) => {
          draft[key] = [];
        });
        Object.entries(scope.filters).forEach(([key, val]) => {
          draft[key] = Array.isArray(val) ? [...val] : [];
        });
      });
      setCommonFiltersSequence(
        Array.isArray(scope.filters_sequence)
          ? [...scope.filters_sequence]
          : [],
      );
      setCommonFiltersIndexes((draft) => {
        Object.entries(scope.filters).forEach(([key, val]) => {
          draft[key] = Array.isArray(val)
            ? getIndexes(
                (filtersItems.current && filtersItems.current[key]) || [],
                val,
              )
            : [];
        });
      });
      setCommonFiltersMode((draft) => {
        Object.entries(scope.filters).forEach(([key]) => {
          draft[key] = "items";
        });
      });

      // Get chart data for the selected currency, with fallback to first available currency
      const allChartData = initiative.chart_data;
      const chartData =
        allChartData?.[selectedCurrency] ||
        (allChartData && Object.values(allChartData)[0]) ||
        null;

      if (chartData) {
        setMonthlySalesChartData(processMonthlySalesData(chartData.sales));
        setServeMarginMonthlyChartData(
          processMonthlyServeMarginData(chartData.serve_margin),
        );
      } else {
        setMonthlySalesChartData({ datasets: [], labels: [] });
        setServeMarginMonthlyChartData({ datasets: [], labels: [] });
      }

      // Validate form fields after loading initiative data
      setTimeout(() => {
        // Validate short description (required for all stages)
        const shortDesc = initiative.short_description || "";
        const shortDescValid = shortDesc.length >= 3 && shortDesc.length <= 60;
        setInitiativeFormValue("shortDescriptionStatus", shortDescValid);

        // Validate leader
        const leader = initiative.leader || "";
        const stage = initiative.stage || "opportunity";
        let leaderValid;
        if (stage === "opportunity") {
          // For opportunity stage, leader is optional (empty or valid)
          leaderValid =
            leader.length === 0 || (leader.length >= 2 && leader.length <= 20);
        } else {
          // For other stages, leader is mandatory
          leaderValid = leader.length >= 2 && leader.length <= 20;
        }
        setInitiativeFormValue("leaderStatus", leaderValid);

        // Validate benefit (must be greater than 0, required for all stages except opportunity)
        const benefit = parseFloat(initiative.margin_benefit || 0);
        let benefitValid;
        if (stage === "opportunity") {
          // For opportunity stage, benefit is optional (can be 0)
          benefitValid = !isNaN(benefit) && benefit >= 0 && benefit <= 0.2; // 0.2 = 20%
        } else {
          // For other stages, benefit must be greater than 0
          benefitValid = !isNaN(benefit) && benefit > 0 && benefit <= 0.2; // 0.2 = 20%
        }
        setInitiativeFormValue("benefitStatus", benefitValid);

        // Validate dates
        const projectStart = initiative.project_start
          ? moment(initiative.project_start, "YYYY-MM")
          : null;
        const benefitStart = initiative.margin_benefit_start
          ? moment(initiative.margin_benefit_start, "YYYY-MM")
          : null;
        const benefitEnd = initiative.margin_benefit_end
          ? moment(initiative.margin_benefit_end, "YYYY-MM")
          : null;

        // Basic date validation
        const projectStartValid =
          projectStart && projectStart.isAfter(moment().subtract(1, "year"));
        const benefitStartValid =
          benefitStart && benefitStart.isAfter(moment().subtract(1, "year"));
        const benefitEndValid =
          benefitEnd && benefitEnd.isAfter(moment().subtract(1, "year"));

        // Additional validation for date relationships
        let finalProjectStartValid = projectStartValid;
        let finalBenefitStartValid = benefitStartValid;
        let finalBenefitEndValid = benefitEndValid;

        // If all dates are present, validate chronological order
        if (projectStart && benefitStart && benefitEnd) {
          finalProjectStartValid = projectStart.isSameOrAfter(
            moment().startOf("month"),
          );
          finalBenefitStartValid = benefitStart.isSameOrAfter(projectStart);
          finalBenefitEndValid = benefitEnd.isSameOrAfter(benefitStart);
        }

        // For opportunity stage, dates are optional
        if (stage === "opportunity") {
          finalProjectStartValid = true;
          finalBenefitStartValid = true;
          finalBenefitEndValid = true;
        }

        setInitiativeFormValue("projectStartStatus", finalProjectStartValid);
        setInitiativeFormValue("benefitStartStatus", finalBenefitStartValid);
        setInitiativeFormValue("benefitEndStatus", finalBenefitEndValid);
      }, 0);
    },
    [selectedCurrency, processMonthlySalesData, processMonthlyServeMarginData],
  );
  const handleInitiativeClick = useCallback(
    (id) => {
      if (id === selectedInitiative) return false;

      // Show/hide chart overlay based on whether the selected initiative has overlay active
      setShowChartOverlay(overlayInitiativeIds.has(id));

      setSelectedInitiative(id);
      updateInitiativeData(initiatives[id]);

      if (layoutMode === "expanded") {
        setLayoutMode("collapsed");
      }

      const isNew = id === "new";
      if (isNew) {
        setAllFormStatuses(false);
      }

      // Auto-expand the stage of the selected initiative
      if (!isNew && initiatives[id]) {
        setCollapsedStages((s) => {
          s.delete(initiatives[id].stage);
          return s;
        });
      }
    },
    [
      selectedInitiative,
      layoutMode,
      initiatives,
      stages,
      updateInitiativeData,
      overlayInitiativeIds,
    ],
  );

  // Helper function to check initiative status
  const hasStatus = useCallback((init, status) => init.status === status, []);
  const isActive = useCallback(
    (init) => hasStatus(init, "active"),
    [hasStatus],
  );
  const isOnHold = useCallback(
    (init) => hasStatus(init, "on_hold"),
    [hasStatus],
  );
  const isCancelled = useCallback(
    (init) => hasStatus(init, "cancelled"),
    [hasStatus],
  );
  const isFinished = useCallback(
    (init) => hasStatus(init, "finished"),
    [hasStatus],
  );

  // Helper functions for sorting and validation
  const compareValues = (x, y, reverse = false) => {
    const result = x < y ? 1 : x > y ? -1 : 0;
    return reverse ? -result : result;
  };

  const compareDates = (x, y, reverse = false) => {
    const xMoment = moment(x, "YYYY-MM");
    const yMoment = moment(y, "YYYY-MM");
    const daysDiff = moment.duration(xMoment.diff(yMoment)).asDays();
    const result = daysDiff < 0 ? -1 : daysDiff > 0 ? 1 : 0;
    return reverse ? -result : result;
  };

  const compareDateTime = (x, y, reverse = false) => {
    const xMoment = moment.utc(x, "YYYY-MM-DD HH:mm").local();
    const yMoment = moment.utc(y, "YYYY-MM-DD HH:mm").local();
    const daysDiff = moment.duration(xMoment.diff(yMoment)).asDays();
    const result = daysDiff < 0 ? -1 : daysDiff > 0 ? 1 : 0;
    return reverse ? -result : result;
  };

  // Helper function to apply filter conditions
  const applyFilter = (
    condition,
    value,
    filterValue,
    operation = "includes",
  ) => {
    if (!condition || !filterValue) return true;

    switch (operation) {
      case "includes":
        return filterValue.includes(value);
      case "equals":
        return value === filterValue;
      case "dateFrom":
        return value && value >= filterValue;
      case "dateTo":
        return value && value <= filterValue;
      case "tags": {
        // If no tags are selected in the filter, show all initiatives
        if (!filterValue || filterValue.length === 0) return true;
        // If initiative has no tags but filter has tags, don't show it
        if (!value || value.length === 0) return false;
        // Ensure both arrays contain the same data type (strings or numbers)
        const normalizedValue = value.map((v) => String(v));
        const normalizedFilterValue = filterValue.map((v) => String(v));
        // Show initiative if it has any of the selected tags
        return normalizedValue.some((tagId) =>
          normalizedFilterValue.includes(tagId),
        );
      }
      case "singleTag": {
        // If no tag is selected in the filter, show all initiatives
        if (!filterValue) return true;
        // If initiative has no tags but filter has a tag, don't show it
        if (!value || value.length === 0) return false;
        // Show initiative if it has the selected tag
        return value.includes(filterValue);
      }
      default:
        return true;
    }
  };

  const handleDeleteInitiative = useCallback(async () => {
    if (!selectedInitiative || selectedInitiative === "new") {
      notify(t("initiatives.errors.generic_error"), "error");
      return;
    }

    try {
      const response = await client.post("api/initiatives_delete_initiative/", {
        initiative_id: selectedInitiative,
      });

      if (response.data && response.data.success === true) {
        // Remove the initiative from the initiatives state
        setInitiatives((draft) => {
          delete draft[selectedInitiative];
        });

        // Clear the selected initiative and remove it from overlay list
        setSelectedInitiative(null);
        setShowChartOverlay(false);
        setOverlayInitiativeIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(selectedInitiative);
          return newSet;
        });

        // Reset the initiative form to initial state
        setInitiativeForm({
          shortDescription: "",
          shortDescriptionPrinted: "",
          shortDescriptionStatus: false,
          leader: "",
          leaderStatus: false,
          benefit: "0.00",
          benefitStatus: false,
          benefitGlobal: "0.00",
          scopeMarginRatio: "100.00",
          projectStart: null,
          projectStartStatus: false,
          benefitStart: null,
          benefitStartStatus: false,
          benefitEnd: null,
          benefitEndStatus: false,
          longDescription: "",
          stage: "opportunity",
          pnlImpactLine: null,
          scope: [],
          tags: [],
        });

        // Show success message
        notify(t("initiatives.delete_success"), "success");

        // Go back to board view by simulating back to list button click
        toggleLayout();
      } else {
        // Handle unsuccessful response
        notify(t("initiatives.errors.generic_error"), "error");
      }
    } catch (error) {
      console.error("Error deleting initiative:", error);
      notify(t("initiatives.errors.generic_error"), "error");
    } finally {
      setIsDeletingInitiative(false);
      setDeleteInitiativeModalOpen(false);
    }
  }, [
    selectedInitiative,
    setInitiatives,
    setSelectedInitiative,
    t,
    notify,
    toggleLayout,
  ]);

  const handlers = {
    deleteInitiative: () => {
      setDeleteInitiativeModalOpen(true);
    },
    cancelAddition: () => {
      setSelectedInitiative(null);
      setShowChartOverlay(false);
      if (layoutMode === "collapsed") {
        setLayoutMode("expanded");
      }
    },

    addUpdate: () => {
      setAddUpdateModalOpen(true);
    },
    clearFilters: () => {
      setTempFilters({
        statuses: ["active", "on_hold", "cancelled", "finished"],
        project_start_after: null,
        capture_end_before: null,
        tag_ids: [],
      });
    },
    applyFilters: () => {
      const dateRanges = [
        {
          name: "Project Start",
          from: tempFilters.projectStartDate.from,
          to: tempFilters.projectStartDate.to,
        },
        {
          name: "Capture Start",
          from: tempFilters.captureStartDate.from,
          to: tempFilters.captureStartDate.to,
        },
        {
          name: "Capture End",
          from: tempFilters.captureEndDate.from,
          to: tempFilters.captureEndDate.to,
        },
        {
          name: "Last Update",
          from: tempFilters.lastUpdate.from,
          to: tempFilters.lastUpdate.to,
        },
      ];

      const invalidRanges = dateRanges.filter((range) => {
        if (range.from && range.to) {
          const fromDate = new Date(range.from);
          const toDate = new Date(range.to);
          return fromDate > toDate;
        }
        return false;
      });

      if (invalidRanges.length > 0) {
        const invalidRangeNames = invalidRanges
          .map((range) => range.name)
          .join(", ");
        notify(
          `Invalid date ranges: ${invalidRangeNames}. "From" date must be before or equal to "To" date.`,
          "error",
        );
        return;
      }

      setActiveFilters(tempFilters);
      setFilterModalOpen(false);
    },

    addInitiative: () => {
      handleInitiativeClick("new");
      setTimeout(() => {
        if (
          shortDescriptionInput.current &&
          shortDescriptionInput.current.firstChild
        ) {
          shortDescriptionInput.current.firstChild.focus();
        }
      }, 100);
    },
  };

  // Currency selection handler
  const handleCurrencySelection = useCallback(
    (option) => {
      if (option !== selectedCurrency) {
        setSelectedCurrency(option);

        // If there's a currently selected initiative, update the chart data states with new currency
        if (
          selectedInitiative &&
          selectedInitiative !== "new" &&
          initiatives[selectedInitiative]
        ) {
          const selectedInitiativeData = initiatives[selectedInitiative];
          const chartData = selectedInitiativeData.chart_data?.[option];

          if (chartData) {
            setMonthlySalesChartData(processMonthlySalesData(chartData.sales));
            setServeMarginMonthlyChartData(
              processMonthlyServeMarginData(chartData.serve_margin),
            );
          }
        }
      }
    },
    [selectedCurrency, selectedInitiative, initiatives],
  );

  // Get currency options for dropdown
  const getCurrencyOptions = useCallback(() => {
    return currencyOptions.map((currency) => ({
      identified: currency,
      text: currency,
      active: currency === selectedCurrency,
      disabled: !currencyOptionsAvailable.includes(currency),
    }));
  }, [currencyOptions, currencyOptionsAvailable, selectedCurrency]);

  const inputsStatusOk = () => {
    // For opportunity stage, only name is required (leader is optional)
    if (initiativeForm.stage === "opportunity") {
      return [initiativeForm.shortDescriptionStatus].every(Boolean);
    }

    // For other stages, all mandatory fields are required
    return [
      initiativeForm.shortDescriptionStatus,
      initiativeForm.leaderStatus,
      initiativeForm.benefitStatus,
      initiativeForm.projectStartStatus,
      initiativeForm.benefitStartStatus,
      initiativeForm.benefitEndStatus,
    ].every(Boolean);
  };

  const validateMandatoryFieldsForStageChange = () => {
    // For opportunity stage, only name is required (leader is optional)
    if (initiativeForm.stage === "opportunity") {
      return [initiativeForm.shortDescriptionStatus].every(Boolean);
    }

    // For other stages, all mandatory fields are required
    return [
      initiativeForm.shortDescriptionStatus,
      initiativeForm.leaderStatus,
      initiativeForm.benefitStatus,
      initiativeForm.projectStartStatus,
      initiativeForm.benefitStartStatus,
      initiativeForm.benefitEndStatus,
    ].every(Boolean);
  };

  // Validation function for drag and drop that works with raw initiative data
  const validateInitiativeForStageChange = (initiative) => {
    if (!initiative) return false;

    // Helper function to validate text fields
    const validateText = (text, minLength, maxLength) => {
      const stripped = text ? text.replace(/^\s+|\s+$/g, "") : "";
      return stripped.length >= minLength && stripped.length <= maxLength;
    };

    // Helper function to validate dates
    const validateDate = (date) => {
      if (!date) return false;
      try {
        const momentDate = moment.isMoment(date)
          ? date
          : moment(date, "YYYY-MM");
        return (
          momentDate.isValid() &&
          momentDate.isAfter(moment().subtract(1, "year"))
        );
      } catch (error) {
        return false;
      }
    };

    // Helper function to validate benefit
    const validateBenefit = (benefit) => {
      const num = parseFloat(benefit);
      return !isNaN(num) && num >= 0 && num <= 20;
    };

    // Validate all mandatory fields
    const shortDescriptionValid = validateText(
      initiative.short_description,
      3,
      60,
    );

    // For opportunity stage, leader is optional (empty or valid)
    let leaderValid;
    if (initiative.stage === "opportunity") {
      const strippedLeader = initiative.leader
        ? initiative.leader.replace(/^\s+|\s+$/g, "")
        : "";
      leaderValid =
        strippedLeader.length === 0 ||
        (strippedLeader.length >= 2 && strippedLeader.length <= 20);
    } else {
      // For other stages, leader is mandatory
      leaderValid = validateText(initiative.leader, 2, 20);
    }

    const benefitValid = validateBenefit(initiative.margin_benefit * 100); // Convert from decimal to percentage
    const projectStartValid = validateDate(initiative.project_start);
    const benefitStartValid = validateDate(initiative.margin_benefit_start);
    const benefitEndValid = validateDate(initiative.margin_benefit_end);

    return [
      shortDescriptionValid,
      leaderValid,
      benefitValid,
      projectStartValid,
      benefitStartValid,
      benefitEndValid,
    ].every(Boolean);
  };

  const commitCurrentInit = (init) => {
    setInitiatives((draft) => {
      draft[init.id] = {
        ...draft[init.id],
        ...init,
      };
    });
  };
  const checkAndSaveInitiative = () => {
    // The initial checks are now just for validation status.
    // The isSavePending check is redundant because the button would be disabled if it were false.
    if (!inputsStatusOk()) {
      notify(t("initiatives.save_error_input_errors"), "error", 4000);
      return;
    }

    setInitiativesBusy(true);

    const initiativePayload = {
      id: selectedInitiative,
      short_description: initiativeForm.shortDescription,
      long_description: initiativeForm.longDescription,
      leader: initiativeForm.leader,
      margin_benefit: parseFloat(initiativeForm.benefit) / 100,
      margin_benefit_global: parseFloat(initiativeForm.benefitGlobal) / 100,
      margin_benefit_ratio: parseFloat(initiativeForm.scopeMarginRatio) / 100,
      scope: initiativeForm.scope,
      stage: initiativeForm.stage,
      tags: initiativeForm.tags,
      pnl_impact_line: initiativeForm.pnlImpactLine,
      project_start: initiativeForm.projectStart?.format("YYYY-MM"),
      margin_benefit_start: initiativeForm.benefitStart?.format("YYYY-MM"),
      margin_benefit_end: initiativeForm.benefitEnd?.format("YYYY-MM"),
    };

    client
      .post("api/initiatives_save_initiative/", initiativePayload)
      .then((response) => {
        if (response.data.success) {
          let savedInitiative;
          if (initiativePayload.id === "new") {
            savedInitiative = { ...initiativePayload };
            savedInitiative.id = response.data.id;
            savedInitiative.status = "active";
            savedInitiative.follow_up = response.data.follow_up;
            savedInitiative.chart_data = response.data.chart_data || {};
            setSelectedInitiative(response.data.id);
          } else {
            // Use the updated initiative data from backend for existing initiatives
            savedInitiative = response.data.updated_initiative;
          }
          commitCurrentInit(savedInitiative);

          // Update form state to match the saved data to clear the save button
          // But preserve existing chart data to avoid losing it
          if (initiativePayload.id !== "new") {
            // Update form fields without affecting chart data
            updateInitiativeForm({
              shortDescription: savedInitiative.short_description,
              shortDescriptionPrinted: savedInitiative.short_description,
              leader: savedInitiative.leader,
              benefit: (100 * savedInitiative.margin_benefit).toFixed(
                benefitDecimals,
              ),
              benefitGlobal: (
                100 * savedInitiative.margin_benefit_global
              ).toFixed(benefitDecimals),
              scopeMarginRatio: (
                100 * savedInitiative.margin_benefit_ratio
              ).toFixed(benefitDecimals),
              benefitStart: savedInitiative.margin_benefit_start
                ? moment(savedInitiative.margin_benefit_start, "YYYY-MM")
                : null,
              benefitEnd: savedInitiative.margin_benefit_end
                ? moment(savedInitiative.margin_benefit_end, "YYYY-MM")
                : null,
              projectStart: savedInitiative.project_start
                ? moment(savedInitiative.project_start, "YYYY-MM")
                : null,
              longDescription: savedInitiative.long_description,
              stage: savedInitiative.stage || "opportunity",
              pnlImpactLine: savedInitiative.pnl_impact_line || null,
              scope: savedInitiative.scope,
              tags: savedInitiative.tags
                ? savedInitiative.tags.map((t) => t.id)
                : [],
            });

            // Update scope filters
            const scope = savedInitiative.scope[0];

            setCommonFilters((draft) => {
              Object.keys(draft).forEach((key) => {
                draft[key] = [];
              });
              Object.entries(scope.filters).forEach(([key, val]) => {
                draft[key] = Array.isArray(val) ? [...val] : [];
              });
            });

            setCommonFiltersSequence(
              Array.isArray(scope.filters_sequence)
                ? [...scope.filters_sequence]
                : [],
            );

            setCommonFiltersIndexes((draft) => {
              Object.entries(scope.filters).forEach(([key, val]) => {
                draft[key] = Array.isArray(val)
                  ? getIndexes(
                      (filtersItems.current && filtersItems.current[key]) || [],
                      val,
                    )
                  : [];
              });
            });

            setCommonFiltersMode((draft) => {
              Object.entries(scope.filters).forEach(([key]) => {
                draft[key] = "items";
              });
            });
          }

          notify(t("initiatives.save_success"), "success");
        } else {
          notify(
            t(
              "initiatives.errors." + response.data.error_code,
              t("initiatives.errors.generic_error"),
            ),
            "error",
            5000,
          );
        }
      })
      .catch((error) => {
        console.error("Save Initiative Error:", error);
        notify(t("initiatives.errors.save_add_update"), "error", 5000);
      })
      .finally(() => {
        setInitiativesBusy(false);
      });
  };
  const getSortedInitiativesList = useCallback(() => {
    // This function guarantees inactive initiatives to come always at the bottom
    const postSort = (list) => {
      return list.sort((a, b) => {
        if (a.follow_up && b.follow_up) {
          const x = Math.min(a.follow_up[0].traffic_light, 1);
          const y = Math.min(b.follow_up[0].traffic_light, 1);
          return compareValues(x, y);
        } else {
          return 0;
        }
      });
    };
    if (initiativesSortedBy === "benefit_global") {
      return postSort(
        Object.values(initiatives).sort((a, b) =>
          compareValues(a.margin_benefit_global, b.margin_benefit_global),
        ),
      );
    } else if (initiativesSortedBy === "benefit") {
      return postSort(
        Object.values(initiatives).sort((a, b) =>
          compareValues(a.margin_benefit, b.margin_benefit),
        ),
      );
    } else if (initiativesSortedBy === "description") {
      return postSort(
        Object.values(initiatives).sort((a, b) =>
          compareValues(a.short_description, b.short_description, true),
        ),
      );
    } else if (initiativesSortedBy === "leader") {
      return postSort(
        Object.values(initiatives).sort((a, b) =>
          compareValues(a.leader, b.leader, true),
        ),
      );
    } else if (initiativesSortedBy === "benefit_start") {
      return postSort(
        Object.values(initiatives).sort((a, b) =>
          compareDates(a.margin_benefit_start, b.margin_benefit_start, true),
        ),
      );
    } else if (initiativesSortedBy === "status") {
      return postSort(
        Object.values(initiatives).sort((a, b) => {
          const x = a.follow_up[0].traffic_light;
          const y = b.follow_up[0].traffic_light;
          const x2 = statusOrder[a.status] || 0;
          const y2 = statusOrder[b.status] || 0;
          return compareValues(x, y) || compareValues(x2, y2, true);
        }),
      );
    } else if (initiativesSortedBy === "last_update") {
      return postSort(
        Object.values(initiatives).sort((a, b) =>
          compareDateTime(a.follow_up[0].date, b.follow_up[0].date, true),
        ),
      );
    }
    return Object.values(initiatives);
  }, [initiatives, initiativesSortedBy]);

  const getUniqueLeaders = useCallback(() => {
    const leaders = new Set();
    Object.values(initiatives).forEach((init) => {
      if (init.leader && init.status !== "new") {
        leaders.add(init.leader);
      }
    });
    return Array.from(leaders).sort();
  }, [initiatives]);

  const getSortedFilteredInitiativesList = useCallback(() => {
    return getSortedInitiativesList()
      .filter((init) => {
        let enter = true;

        // Skip new initiatives
        if (init.status === "new") return false;

        // Apply simplified filters
        if (
          !applyFilter(
            enter && newFilters.stage !== null,
            init.stage,
            newFilters.stage,
            "equals",
          )
        )
          return false;
        if (
          !applyFilter(
            enter && newFilters.leader !== null,
            init.leader,
            newFilters.leader,
            "equals",
          )
        )
          return false;
        if (
          !applyFilter(
            enter && newFilters.tags !== null,
            (init.tags || []).map((tag) => tag.id),
            newFilters.tags,
            "singleTag",
          )
        )
          return false;

        // Apply legacy filters (keep for backward compatibility)
        if (enter && !initiativesFilters.active && isActive(init)) return false;
        if (enter && !initiativesFilters.on_hold && isOnHold(init))
          return false;
        if (enter && !initiativesFilters.cancelled && isCancelled(init))
          return false;
        if (enter && !initiativesFilters.finished && isFinished(init))
          return false;
        if (
          enter &&
          isActive(init) &&
          !initiativesFilters.status1 &&
          init.follow_up[0].traffic_light === 1
        )
          return false;
        if (
          enter &&
          isActive(init) &&
          !initiativesFilters.status2 &&
          init.follow_up[0].traffic_light === 2
        )
          return false;
        if (
          enter &&
          isActive(init) &&
          !initiativesFilters.status3 &&
          init.follow_up[0].traffic_light === 3
        )
          return false;
        if (
          enter &&
          !initiativesFilters.updated &&
          moment
            .duration(
              moment()
                .startOf("day")
                .diff(
                  moment
                    .utc(init.follow_up[0].date, "YYYY-MM-DD HH:mm")
                    .local(),
                ),
            )
            .asDays() <= 28
        )
          return false;
        if (
          enter &&
          !initiativesFilters.outdated &&
          moment
            .duration(
              moment()
                .startOf("day")
                .diff(
                  moment
                    .utc(init.follow_up[0].date, "YYYY-MM-DD HH:mm")
                    .local(),
                ),
            )
            .asDays() > 28
        )
          return false;

        return true;
      })
      .map((init) => {
        return {
          ...init,
          initiative: init.short_description,
          displayStatus: getInitiativeDisplayStatus(init),
        };
      });
  }, [
    getSortedInitiativesList,
    newFilters,
    applyFilter,
    initiativesFilters,
    isActive,
  ]);

  const commitAddUpdate = (id, update) => {
    // const statusMap = {
    //   on_hold: "on_hold",
    //   cancelling: "cancelled",
    //   finishing: "finished",
    // };
    // const new_status = statusMap[update.event_type] || "active";
    const new_status = "active";

    setInitiatives((draft) => {
      draft[id].status = new_status;
      draft[id].follow_up = [update, ...draft[id].follow_up];
    });
  };
  const saveAddUpdate = (updateText, trafficLight = 1) => {
    setInitiativesBusy(true);
    let init_id = selectedInitiative;
    let update = {
      initiative_id: init_id,
      event_type: "update",
      traffic_light: trafficLight,
      description: updateText,
    };
    client
      .post("api/initiatives_add_update/", update)
      .then((response) => {
        if (response.data.success) {
          update = {
            ...update,
            id: response.data.id,
            user_name: response.data.user_name,
            date: response.data.date,
          };
          commitAddUpdate(init_id, update);
          setAddUpdateModalOpen(false);
        } else {
          notify(t("initiatives.errors.save_add_update"), "error", 5000);
        }
        setInitiativesBusy(false);
      })
      .catch(() => {
        notify(t("initiatives.errors.save_add_update"), "error", 5000);
        setInitiativesBusy(false);
      });
  };
  const filtersListTranslated = useCallback(() => {
    return (filtersList.current || []).map((filter) => {
      if (Object.keys(filtersNames.current || {}).includes(filter)) {
        return (filtersNames.current || {})[filter][i18n.language];
      } else {
        return filter;
      }
    });
  }, [i18n.language]);
  const filtersActiveList = useCallback(() => {
    const activeFilters = Object.keys(filtersNames.current || {}).filter(
      (filter) => commonFilters[filter] && commonFilters[filter].length > 0,
    );
    // Return indexes instead of filter names
    return activeFilters
      .map((filter) => (filtersList.current || []).indexOf(filter))
      .filter((index) => index !== -1);
  }, [commonFilters]);
  const getIndexes = useCallback((filters, values) => {
    // Safety check for filters
    if (!filters || !Array.isArray(filters)) {
      return values.map(() => -1);
    }
    return values.map((item) => filters.indexOf(item));
  }, []);
  const filterMenuClickHandler = useCallback(
    () => (event) => {
      const selectedFilter =
        typeof event === "string" ? event : (filtersList.current || [])[event];
      setFilterModalCurrentFilter(selectedFilter);
      setFilterModalFilterName(
        (filtersNames.current || {})[selectedFilter]?.[i18n.language] ||
          selectedFilter,
      );

      let choicesList = (filtersItems.current || {})[selectedFilter] || [];
      const transformedChoices = choicesList.map((item, index) => ({
        display: item === "" ? "* " + t("results.blank") + " *" : item,
        originalIndex: index,
      }));

      setFilterModalItemsChoices(transformedChoices);

      setFilterModalFilterMode(commonFiltersMode[selectedFilter]);
      if (commonFiltersMode[selectedFilter] === "items") {
        setFilterModalItemsSelected(commonFiltersIndexes[selectedFilter]);
      } else {
        setFilterModalItemsSelected([]);
      }
      setFilterModalOpen(true);
    },
    [i18n.language, commonFiltersMode, commonFiltersIndexes],
  );
  const filtersSequence = useCallback(() => {
    return (filtersList.current || []).map((filter) => {
      // Safety check for commonFiltersSequence
      if (!commonFiltersSequence || !Array.isArray(commonFiltersSequence)) {
        return "";
      }
      const index = commonFiltersSequence.indexOf(filter);
      return index === -1 ? "" : index > 9 ? "N" : (index + 1).toString();
    });
  }, [commonFiltersSequence]);
  const getFilterItems = useCallback(
    (raw_items, filter, mode) => {
      // Safety check for raw_items
      if (!raw_items || !Array.isArray(raw_items)) {
        return [];
      }

      if (filter === "period" && mode === "items") {
        return raw_items.map((month) => {
          return (
            t("months." + month[1].toString()) +
            "-" +
            month[0].toString().slice(-2)
          );
        });
      } else {
        return raw_items;
      }
    },
    [t],
  );
  const removeCommonFilterInitiatives = useCallback(
    (filter, item, all = false) => {
      // Safety check for commonFilters[filter]
      if (!commonFilters[filter] || !Array.isArray(commonFilters[filter])) {
        return;
      }

      // Calculate new values for all states
      let newFilters = { ...commonFilters };
      let newIndexes = { ...commonFiltersIndexes };
      let newModes = { ...commonFiltersMode };
      let newSequence = [...commonFiltersSequence];

      if (all) {
        // Clear all items for this filter
        newFilters[filter] = [];
        newIndexes[filter] = [];
        newModes[filter] = "";
        newSequence = newSequence.filter((f) => f !== filter);
      } else {
        // Simple logic: find and remove the item by value
        const currentItems = [...commonFilters[filter]];
        const itemIndex = currentItems.indexOf(item);

        if (itemIndex >= 0) {
          currentItems.splice(itemIndex, 1);
          newFilters[filter] = currentItems;

          // Update indexes - remove the corresponding index
          if (
            commonFiltersIndexes[filter] &&
            commonFiltersIndexes[filter].length > itemIndex
          ) {
            const newIndexesArray = [...commonFiltersIndexes[filter]];
            newIndexesArray.splice(itemIndex, 1);
            newIndexes[filter] = newIndexesArray;
          }

          // If no items left, clear the filter completely
          if (currentItems.length === 0) {
            newModes[filter] = "";
            newSequence = newSequence.filter((f) => f !== filter);
          }
        }
      }

      // Update all states
      setCommonFilters((draft) => {
        Object.assign(draft, newFilters);
      });

      setCommonFiltersIndexes((draft) => {
        Object.assign(draft, newIndexes);
      });

      setCommonFiltersMode((draft) => {
        Object.assign(draft, newModes);
      });

      setCommonFiltersSequence(newSequence);

      // Force re-render
      setFilterUpdateTimestamp(Date.now());

      // Trigger scope change detection if there's a selected initiative
      if (selectedInitiative && initiatives[selectedInitiative]) {
        // Update the initiative's scope to reflect the filter changes
        setInitiativeFormValue("scope", [
          {
            filters: newFilters,
            filters_sequence: newSequence,
          },
        ]);

        // Trigger scope change to update data
        setScopeChanged(true);
      }
    },
    [
      commonFilters,
      commonFiltersIndexes,
      commonFiltersMode,
      commonFiltersSequence,
      selectedInitiative,
      initiatives,
    ],
  );
  const getFiltersNamesInSequence = useCallback(() => {
    // Safety check for commonFiltersSequence and filtersNames.current
    if (
      !commonFiltersSequence ||
      !Array.isArray(commonFiltersSequence) ||
      !filtersNames.current
    ) {
      return [];
    }
    const filters = commonFiltersSequence.filter((f) =>
      Object.keys(filtersNames.current || {}).includes(f),
    );

    // Additional check: only return filters that actually have items
    return filters.filter(
      (f) => commonFilters[f] && commonFilters[f].length > 0,
    );
  }, [commonFiltersSequence, commonFilters]);

  // Helper function to check if there are no scope filters (excluding period)
  const hasNoScopeFilters = useCallback(() => {
    return !Object.keys(commonFilters).some(
      (filter) =>
        filter !== "period" &&
        commonFilters[filter] &&
        commonFilters[filter].length > 0,
    );
  }, [commonFilters]);

  const currentScopeFilters = useCallback(() => {
    const filters = getFiltersNamesInSequence();

    return filters
      .map((filter) => {
        // Double-check: Don't render if the filter has no items or doesn't exist
        if (!commonFilters[filter] || commonFilters[filter].length === 0) {
          return null;
        }

        const items = getFilterItems(
          commonFilters[filter],
          filter,
          commonFiltersMode && commonFiltersMode[filter]
            ? commonFiltersMode[filter]
            : "",
        );

        // Safety check: ensure items is an array
        if (!items || !Array.isArray(items)) {
          return null;
        }

        // Safety check: ensure indexes is an array
        const indexes =
          commonFiltersIndexes &&
          commonFiltersIndexes[filter] &&
          Array.isArray(commonFiltersIndexes[filter])
            ? commonFiltersIndexes[filter]
            : [];

        return (
          <FilterTagInitiatives
            key={`${filter}-${commonFilters[filter].join(",")}-${indexes.join(",")}-${commonFiltersMode && commonFiltersMode[filter] ? commonFiltersMode[filter] : ""}-${filterUpdateTimestamp}`}
            filtersList={filtersList.current || []}
            filtersNames={filtersNames.current || {}}
            filter={filter}
            items={items}
            mode={
              commonFiltersMode && commonFiltersMode[filter]
                ? commonFiltersMode[filter]
                : ""
            }
            nameClickHandler={filterMenuClickHandler()}
            removeItemHandler={removeCommonFilterInitiatives}
            color={"gray"}
          />
        );
      })
      .filter(Boolean); // Remove any null values
  }, [
    getFiltersNamesInSequence,
    commonFilters,
    commonFiltersIndexes,
    commonFiltersMode,
    getFilterItems,
    filterMenuClickHandler,
    removeCommonFilterInitiatives,
    filterUpdateTimestamp,
  ]);
  const removeTagItemHandler = useCallback(
    (filter, item, isClearAll) => {
      if (isClearAll) {
        // Clear all tags
        setInitiativeFormValue("tags", []);
      } else {
        // Remove specific tag
        const tagName = item;
        const tagId = tags.find((t) => t.name === tagName)?.id;
        if (tagId) {
          setInitiativeFormValue(
            "tags",
            initiativeForm.tags.filter((id) => id !== tagId),
          );
        }
      }
    },
    [initiativeForm.tags, tags, setInitiativeFormValue],
  );

  const selectedTagsFilters = useCallback(() => {
    const selectedTags = initiativeForm.tags
      .map((tagId) => {
        const tag = tags.find((t) => t.id === tagId);
        return tag ? tag.name : null;
      })
      .filter(Boolean);

    if (selectedTags.length === 0) return null;

    return (
      <FilterTagInitiatives
        filtersList={["tags"]}
        filtersNames={{ tags: { [i18n.language]: "" } }}
        filter="tags"
        items={selectedTags}
        mode="items"
        nameClickHandler={() => {}}
        removeItemHandler={removeTagItemHandler}
        color={"gray"}
      />
    );
  }, [initiativeForm.tags, tags, i18n.language, removeTagItemHandler]);
  const handleFilterModalItemsMenuSelect = (selectedIndexes) => {
    setFilterModalItemsSelected(selectedIndexes);
    setFilterModalFilterMode(selectedIndexes.length > 0 ? "items" : "");
  };
  const handleFilterModalItemsSelectAll = () => {
    const allIndexes = filterModalItemsChoices.map(
      (item) => item.originalIndex,
    );
    setFilterModalItemsSelected(allIndexes);
    setFilterModalFilterMode("items");
  };
  const handleFilterModalItemsSelectNone = () => {
    setFilterModalItemsSelected([]);
    if (filterModalFilterMode === "items") setFilterModalFilterMode("");
  };
  const handleFilterModalItemsInvertSelection = () => {
    const currentSelection = new Set(filterModalItemsSelected);
    const newSelection = filterModalItemsChoices
      .filter((item) => !currentSelection.has(item.originalIndex))
      .map((item) => item.originalIndex);

    setFilterModalItemsSelected(newSelection);
    setFilterModalFilterMode(newSelection.length > 0 ? "items" : "");
  };
  const handleFilterModalClearFilterButton = () => {
    setCommonFilters((draft) => {
      draft[filterModalCurrentFilter] = [];
    });
    setCommonFiltersIndexes((draft) => {
      draft[filterModalCurrentFilter] = [];
    });
    setCommonFiltersMode((draft) => {
      draft[filterModalCurrentFilter] = "";
    });
    // Safety check for commonFiltersSequence
    if (commonFiltersSequence && Array.isArray(commonFiltersSequence)) {
      let common_filters_sequence = commonFiltersSequence.filter(
        (f) => f !== filterModalCurrentFilter,
      );
      setCommonFiltersSequence(common_filters_sequence);
    } else {
      setCommonFiltersSequence([]);
    }
    setFilterModalOpen(false);
  };
  const handleFilterModalApplyButton = () => {
    if (filterModalFilterMode === "items") {
      setCommonFiltersIndexes((draft) => {
        draft[filterModalCurrentFilter] = filterModalItemsSelected;
      });
      setCommonFilters((draft) => {
        draft[filterModalCurrentFilter] = filterModalItemsSelected.map(
          (index) =>
            (filtersItems.current || {})[filterModalCurrentFilter]?.[index] ||
            "",
        );
      });
      setCommonFiltersMode((draft) => {
        draft[filterModalCurrentFilter] = "items";
      });

      // Safety check for commonFiltersSequence
      const currentSequence =
        commonFiltersSequence && Array.isArray(commonFiltersSequence)
          ? [...commonFiltersSequence]
          : [];

      if (!currentSequence.includes(filterModalCurrentFilter)) {
        currentSequence.push(filterModalCurrentFilter);
        setCommonFiltersSequence(currentSequence);
      }
    }

    setFilterModalOpen(false);
  };
  const filtersDialog = (
    <FilterDialog
      open={filterModalOpen}
      onClose={() => setFilterModalOpen(false)}
      headerTitle={filterModalFilterName}
      headerScope={t("initiatives.scope_filters")}
      allItems={filterModalItemsChoices}
      selectedItems={filterModalItemsSelected}
      onItemsSelect={handleFilterModalItemsMenuSelect}
      onSelectAll={handleFilterModalItemsSelectAll}
      onSelectNone={handleFilterModalItemsSelectNone}
      onInvertSelection={handleFilterModalItemsInvertSelection}
      onClearFilter={handleFilterModalClearFilterButton}
      onApply={handleFilterModalApplyButton}
      groupChoices={[]}
      groupSelected={""}
      onGroupSelect={() => {}}
      filterMode={filterModalFilterMode}
      clientColor={clientColor}
    />
  );

  // Calculate stage summaries
  const calculateStageSummary = () => {
    const summary = {};
    stages.forEach((stage) => {
      const stageInitiatives = getSortedFilteredInitiativesList().filter(
        (init) => init.stage === stage,
      );
      summary[stage] = {
        count: stageInitiatives.length,
        global_benefit: stageInitiatives.reduce(
          (sum, init) => sum + init.margin_benefit_global * 100,
          0,
        ),
      };
    });
    return summary;
  };
  // Helper component for stage numbers
  const StageNumber = ({ label, value, grow = false }) => {
    const isPercentage = typeof value === "string" && value.includes("p.p");
    const displayValue = isPercentage ? value.replace(" p.p", "") : value;
    return (
      <div
        style={{
          flex: grow ? 1 : "unset",
          textAlign: "center",
          minWidth: "80px",
          width: "50%",
          margin: "2px 0 4px",
          color: "#555555",
        }}
      >
        <div
          style={{
            fontWeight: 300,
            fontSize: "0.75em",
            textTransform: "lowercase",
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: "1.5em",
          }}
        >
          {displayValue}
          {isPercentage && (
            <span
              style={{
                fontSize: "0.6em",
              }}
            >
              {" p.p."}
            </span>
          )}
        </div>
      </div>
    );
  };
  StageNumber.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    grow: PropTypes.bool,
  };

  // Chart overlay component
  const ChartOverlay = () => (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(242, 242, 242, 0.7)",
        backdropFilter: "blur(1px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10,
        borderRadius: "4px",
      }}
    >
      <div
        style={{
          textAlign: "center",
          color: "#666",
          fontSize: "16px",
          fontWeight: 300,
          padding: "20px",
          maxWidth: "80%",
        }}
      >
        {t("initiatives.chart_updating_message")}
      </div>
    </div>
  );
  // Drag and drop handler
  const onDragEndInitiatives = useCallback(
    (result) => {
      const { destination, source, draggableId } = result;

      if (!destination) return;
      if (destination.droppableId === source.droppableId) return;

      const initiativeId = draggableId;
      const newStage = destination.droppableId;
      const currentStage = source.droppableId;

      // Get the initiative data
      const initiative = initiatives[initiativeId];
      if (!initiative) return;

      // Validate stage change: if moving from opportunity to other stages, check mandatory fields
      if (currentStage === "opportunity" && newStage !== "opportunity") {
        if (!validateInitiativeForStageChange(initiative)) {
          notify(t("initiatives.errors.mandatory_fields_required"), "error");
          return;
        }
      }

      // Update local state immediately so the initiative appears in the new stage
      setInitiatives((draft) => {
        if (draft[initiativeId]) {
          draft[initiativeId].stage = newStage;
        }
      });

      setLoadingInitiativeId(initiativeId);

      // API call to update stage
      client
        .post("api/initiatives_update_stage/", {
          initiative_id: initiativeId,
          stage: newStage,
        })
        .then((response) => {
          if (response.data.success) {
            notify(t("initiatives.stage_updated_successfully"), "success");
          } else {
            // Revert the change if the API call failed
            setInitiatives((draft) => {
              if (draft[initiativeId]) {
                draft[initiativeId].stage = source.droppableId;
              }
            });
            notify(t("initiatives.stage_update_failed"), "error");
          }
        })
        .catch((error) => {
          console.error("Stage update error:", error);
          // Revert the change if the API call failed
          setInitiatives((draft) => {
            if (draft[initiativeId]) {
              draft[initiativeId].stage = source.droppableId;
            }
          });
          notify(t("initiatives.stage_update_failed"), "error");
        })
        .finally(() => {
          setLoadingInitiativeId(null);
        });
    },
    [initiatives, validateInitiativeForStageChange, notify, t],
  );
  const handleViewChange = useCallback(
    (view) => {
      setInitiativesView(view);
      if (layoutMode === "collapsed") {
        // list is small ⇒ close details panel
        setSelectedInitiative(null);
        setShowChartOverlay(false);
        setLayoutMode("expanded");
      }
    },
    [layoutMode],
  );
  // Create filtered list for board view
  const sortedAndFilteredInitiatives = useMemo(
    () => getSortedFilteredInitiativesList(),
    [
      initiatives,
      initiativesSortedBy,
      newFilters,
      initiativesFilters,
      i18n.language,
    ],
  );

  // const LIST_MD_COLLAPSED = 3;
  //const COLLAPSED_MIN_WIDTH = 230;
  const isCollapsed = layoutMode === "collapsed";

  // Actions - suspended menu
  const Actions = (
    <>
      {/* Add Initiative */}
      <ActionBarButton
        icon={<AddCircle />}
        text={t("initiatives.add_initiative")}
        onClickHandler={handlers.addInitiative}
        disabled={screenBusy || initiativesBusy}
      />
      {/* Filter */}
      <ActionBarButton
        icon={<FilterList />}
        text={t("initiatives.filters")}
        onClickHandler={() => setNewFilterModalOpen(true)}
        disabled={screenBusy || initiativesBusy}
      />
      <ActionBarButton
        icon={<Icon>local_offer</Icon>} // Using a tag icon
        text={t("initiatives.tags")}
        onClickHandler={() => setTagManagementModalOpen(true)}
        disabled={screenBusy || initiativesBusy}
      />
      {/* Currency */}
      <ActionDropdown
        button={
          <ActionBarButton
            icon={<AttachMoney />}
            text={t("initiatives.currency")}
            disabled={!metaLoaded || currencyOptionsAvailable.length === 0}
          />
        }
        dropdownList={getCurrencyOptions()}
        onClick={handleCurrencySelection}
      />
      {/* Download Data 
      <ActionBarButton
        icon={<GetApp />}
        text={t("initiatives.download_data")}
        onClickHandler={handleDownloadButton}
        disabled={screenBusy || initiativesBusy}
      />
      */}
    </>
  );
  const handleSaveNewTag = () => {
    const newName = newTagName.trim();
    if (!newName || tagActionLoading) return;

    setTagActionLoading(true);
    client
      .post("api/initiatives_tags/", { name: newName })
      .then((response) => {
        setTags((prev) =>
          [...prev, response.data].sort((a, b) => a.name.localeCompare(b.name)),
        );
        setNewTagName("");
        setIsAddingTag(false);
        notify(t("initiatives.tag_created_success"), "success");
      })
      .catch(() => notify(t("initiatives.errors.generic_error"), "error"))
      .finally(() => setTagActionLoading(false));
  };
  const handleSaveEditedTag = () => {
    const newName = editingTag.name.trim();
    if (!newName || tagActionLoading) return;

    setTagActionLoading(true);
    client
      .put("api/initiatives_tags/", { id: editingTag.id, name: newName })
      .then((response) => {
        setTags((prev) =>
          prev
            .map((t) => (t.id === editingTag.id ? response.data : t))
            .sort((a, b) => a.name.localeCompare(b.name)),
        );
        setEditingTag(null);
        notify(t("initiatives.tag_updated_success"), "success");
      })
      .catch(() => {
        notify(t("initiatives.errors.generic_error"), "error");
        // Reset to original value on error
        setEditingTag(null);
      })
      .finally(() => setTagActionLoading(false));
  };
  const handleDeleteTag = () => {
    if (!confirmDeleteTag || tagActionLoading) return;

    setTagActionLoading(true);
    client
      .delete(`api/initiatives_tags/`, { data: { id: confirmDeleteTag.id } })
      .then(() => {
        setTags((prev) => prev.filter((t) => t.id !== confirmDeleteTag.id));
        notify(t("initiatives.tag_deleted_success"), "success");
        setConfirmDeleteTag(null);
      })
      .catch(() => notify(t("initiatives.errors.generic_error"), "error"))
      .finally(() => setTagActionLoading(false));
  };
  const handleCancelEditing = () => {
    setEditingTag(null);
  };

  useEffect(() => {
    if (filterModalOpen) {
      setTempFilters(activeFilters);
    }
  }, [filterModalOpen]);

  // Main initiatives card component
  const InitiativesCard = (
    <Collapse in={!isCollapsed} timeout={300}>
      <InitiativesStatsCards initiatives={initiatives} t={t} i18n={i18n} />
      <Card
        className={cx({
          [classes.card]: true,
          [classes.initiativesCard]: true,
        })}
      >
        <CardBody
          className={classes.initiativesCardBodyTableView}
          style={{ padding: "0", marginTop: "-5px" }}
        >
          {/* --- New Header for Expanded View --- */}
          <CardHeader style={{ padding: "0 15px 10px", position: "relative" }}>
            <GridContainer>
              <GridItem>
                <p
                  className={classes.cardCategory}
                  style={{
                    fontWeight: "400",
                    textTransform: "uppercase",
                    paddingRight: "10px",
                  }}
                >
                  {t("initiatives.list_of_initiatives")}
                </p>
              </GridItem>
              <GridItem xs={true}></GridItem>
              <GridItem
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginTop: "15px",
                }}
              >
                {/* --- View Switcher Buttons --- */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    paddingTop: "2px",
                  }}
                >
                  {/* Monthly/Quarterly/Yearly buttons - only show for Gantt view */}
                  {initiativesView === "gantt" && (
                    <div
                      className={cx(classes.viewButtonsContainer)}
                      style={{
                        position: "relative",
                        top: "auto",
                        transform: "none",
                        marginLeft: "0",
                        marginBottom: "0px",
                        marginTop: "0px",
                      }}
                    >
                      <Button
                        size="sm"
                        simple={viewMode !== "month"}
                        className={cx(
                          classes.viewButton,
                          classes.firstButtonDate,
                          {
                            [classes.buttonSelectedDate]: viewMode === "month",
                          },
                        )}
                        onClick={() => setViewMode("month")}
                        style={{ fontSize: "7px !important" }}
                      >
                        {t("initiatives.monthly")}
                      </Button>
                      <Button
                        size="sm"
                        simple={viewMode !== "quarter"}
                        className={cx(
                          classes.viewButton,
                          classes.middleButtonDate,
                          {
                            [classes.buttonSelectedDate]:
                              viewMode === "quarter",
                          },
                        )}
                        onClick={() => setViewMode("quarter")}
                        style={{ fontSize: "7px !important" }}
                      >
                        {t("initiatives.quarterly")}
                      </Button>
                      <Button
                        size="sm"
                        simple={viewMode !== "year"}
                        className={cx(
                          classes.viewButton,
                          classes.lastButtonDate,
                          {
                            [classes.buttonSelectedDate]: viewMode === "year",
                          },
                        )}
                        onClick={() => setViewMode("year")}
                        style={{ fontSize: "7px !important" }}
                      >
                        {t("initiatives.yearly")}
                      </Button>
                    </div>
                  )}
                  {/* Board/Gantt/Table buttons */}
                  <div
                    className={cx(classes.viewButtonsContainer)}
                    style={{
                      position: "relative",
                      top: "auto",
                      transform: "none",
                    }} // Override positioning from styles
                  >
                    <Button
                      size="sm"
                      color="client"
                      simple={initiativesView !== "table"}
                      className={cx(classes.viewButton, classes.firstButton, {
                        [classes.buttonSelected]: initiativesView === "table",
                      })}
                      onClick={() => handleViewChange("table")}
                      disabled={screenBusy || initiativesBusy}
                    >
                      {t("initiatives.table_view")}
                    </Button>
                    <Button
                      size="sm"
                      color="client"
                      simple={initiativesView !== "gantt"}
                      className={cx(classes.viewButton, classes.middleButton, {
                        [classes.buttonSelected]: initiativesView === "gantt",
                      })}
                      onClick={() => handleViewChange("gantt")}
                      disabled={screenBusy || initiativesBusy}
                    >
                      {t("initiatives.timeline_view")}
                    </Button>
                    <Button
                      size="sm"
                      color="client"
                      simple={initiativesView !== "board"}
                      className={cx(classes.viewButton, classes.lastButton, {
                        [classes.buttonSelected]: initiativesView === "board",
                      })}
                      onClick={() => handleViewChange("board")}
                      disabled={screenBusy || initiativesBusy}
                    >
                      {t("initiatives.kanban_view")}
                    </Button>
                  </div>
                </div>
              </GridItem>
            </GridContainer>
          </CardHeader>
          {initiativesView === "table" && (
            <>
              <div
                className={cx({
                  [classes.initiativesRow]: true,
                  [classes.initiativesHeader]: true,
                })}
              >
                <div
                  className={cx({
                    [classes.initiativeShortDescription]: true,
                    [classes.initiativeSorted]:
                      initiativesSortedBy === "description",
                  })}
                  onClick={() => setInitiativesSortedBy("description")}
                >
                  {t("initiatives.description")}
                </div>
                <div
                  className={cx({
                    [classes.initiativeLeader]: true,
                    [classes.initiativeSorted]:
                      initiativesSortedBy === "leader",
                  })}
                  onClick={() => setInitiativesSortedBy("leader")}
                >
                  {t("initiatives.leader")}
                </div>
                <div
                  className={cx({
                    [classes.initiativeGlobalBenefit]: true,
                    [classes.initiativeSorted]:
                      initiativesSortedBy === "benefit_global",
                  })}
                  onClick={() => setInitiativesSortedBy("benefit_global")}
                >
                  {t("initiatives.benefit_global")}
                </div>
                <div
                  className={cx({
                    [classes.initiativeBenefit]: true,
                    [classes.initiativeSorted]:
                      initiativesSortedBy === "benefit",
                  })}
                  onClick={() => setInitiativesSortedBy("benefit")}
                >
                  {t("initiatives.benefit")}
                </div>
                <div
                  className={cx({
                    [classes.initiativeBenefitStart]: true,
                    [classes.initiativeSorted]:
                      initiativesSortedBy === "benefit_start",
                  })}
                  onClick={() => setInitiativesSortedBy("benefit_start")}
                >
                  {t("initiatives.capture_start")}
                </div>
                <div
                  className={cx({
                    [classes.initiativeStatus]: true,
                    [classes.initiativeSorted]:
                      initiativesSortedBy === "status",
                  })}
                  onClick={() => setInitiativesSortedBy("status")}
                >
                  {t("initiatives.status")}
                </div>
                <div
                  className={cx({
                    [classes.initiativeDate]: true,
                    [classes.initiativeSorted]:
                      initiativesSortedBy === "last_update",
                  })}
                  onClick={() => setInitiativesSortedBy("last_update")}
                >
                  {t("initiatives.update_date")}
                </div>
              </div>
              <div
                className={classes.initiativesWrapperTableView}
                ref={initiativesWrapper}
              >
                {getSortedFilteredInitiativesList().map((init, key) => (
                  <div
                    className={cx({
                      [classes.initiativesRow]: true,
                      ["inactive"]: !isActive(init),
                      ["selected"]: init.id === selectedInitiative,
                    })}
                    onClick={() => handleInitiativeClick(init.id)}
                    key={key}
                  >
                    <div
                      className={cx({
                        [classes.initiativeShortDescription]: true,
                      })}
                    >
                      {init.short_description}
                    </div>
                    <div
                      className={cx({
                        [classes.initiativeLeader]: true,
                      })}
                    >
                      {init.leader}
                    </div>
                    <div
                      className={cx({
                        [classes.initiativeGlobalBenefit]: true,
                      })}
                    >
                      {(
                        100 *
                        init.margin_benefit_ratio *
                        init.margin_benefit
                      ).toFixed(benefitDecimals)}
                      %
                    </div>
                    <div
                      className={cx({
                        [classes.initiativeBenefit]: true,
                      })}
                    >
                      {(100 * init.margin_benefit).toFixed(benefitDecimals)}%
                    </div>
                    <div
                      className={cx({
                        [classes.initiativeBenefitStart]: true,
                      })}
                    >
                      {t(
                        "months." +
                          moment(init.margin_benefit_start, "YYYY-MM").format(
                            "M",
                          ),
                      )}
                      {"-"}
                      {moment(init.margin_benefit_start, "YYYY-MM").format(
                        "YY",
                      )}
                    </div>
                    <div
                      className={cx({
                        [classes.initiativeStatus]: true,
                        [classes.initiativeStatusColor]: true,
                        ["selected"]: init.id === selectedInitiative,
                        ["status-" + init.status]: true,
                        ["trafficLight" +
                        (init.follow_up[0].traffic_light || 0).toString()]:
                          true,
                      })}
                    ></div>
                    <div
                      className={cx({
                        [classes.initiativeDate]: true,
                        ["selected"]: init.id === selectedInitiative,
                        ["critical"]:
                          moment
                            .duration(
                              moment()
                                .startOf("day")
                                .diff(
                                  moment
                                    .utc(
                                      init.follow_up[0].date,
                                      "YYYY-MM-DD HH:mm",
                                    )
                                    .local(),
                                ),
                            )
                            .asDays() > 28,
                      })}
                    >
                      {moment
                        .utc(init.follow_up[0].date, "YYYY-MM-DD HH:mm")
                        .local()
                        .format("D")}
                      {"-"}
                      {t(
                        "months." +
                          moment
                            .utc(init.follow_up[0].date, "YYYY-MM-DD HH:mm")
                            .local()
                            .format("M"),
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          {initiativesView === "board" && (
            <div
              style={{
                overflowX: "auto",
                margin: "0 7px 7px 7px",
              }}
            >
              <DragDropContext onDragEnd={onDragEndInitiatives}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${stages.length}, 1fr)`,
                    gap: "8px",
                    overflowX: "hidden",
                    minHeight: "500px",
                  }}
                >
                  {stages.map((stageKey) => {
                    const stageItems = sortedAndFilteredInitiatives
                      .filter((initiative) => initiative.stage === stageKey)
                      .sort((a, b) => {
                        const aStatus = statusOrder[a.displayStatus] || 0;
                        const bStatus = statusOrder[b.displayStatus] || 0;
                        return bStatus - aStatus; // Higher status values first
                      });

                    return (
                      <DroppableStageColumn
                        key={stageKey}
                        stageKey={stageKey}
                        stageItems={stageItems}
                        stageSummary={calculateStageSummary()}
                        newFilters={newFilters}
                        statusOrder={statusOrder}
                        loadingInitiativeId={loadingInitiativeId}
                        handleInitiativeClick={handleInitiativeClick}
                        t={t}
                        StageNumber={StageNumber}
                      />
                    );
                  })}
                </div>
              </DragDropContext>
            </div>
          )}
          {initiativesView === "gantt" && (
            <ConsolidatedInitiativesGantt
              initiatives={sortedAndFilteredInitiatives.map(toGanttRow)}
              classes={classes}
              t={t}
              onSelect={(id) => handleInitiativeClick(id)}
              viewMode={viewMode}
              setViewMode={setViewMode}
            />
          )}
        </CardBody>
      </Card>
    </Collapse>
  );

  return (
    <div>
      <ActionBar actions={Actions} spinnerIn={screenBusy || initiativesBusy} />
      <GridContainer style={{ marginTop: "2px" }}>
        <GridItem
          xs={12}
          style={{
            transition: "flex-basis .3s ease-in-out",
            marginBottom: "2px",
          }}
        >
          <div className={classes.cardsFullHeight}>
            <div className={classes.cardsWrapper}></div>
            <div className={classes.fullHeight}>{InitiativesCard}</div>
          </div>
        </GridItem>
        {/* Compact mini‑list */}
        {isCollapsed && (
          <GridItem xs={12} md={3}>
            <div
              style={{
                paddingRight: "10px",
                borderRight: `1px solid ${clientColor}`,
                marginTop: "-15px",
              }}
            >
              <Card
                className={cx({
                  [classes.card]: true,
                  [classes.initiativesCard]: true,
                })}
              >
                <CardBody
                  className={classes.initiativesCardBodyTableView}
                  style={{ padding: "0", marginTop: "-5px" }}
                >
                  <div
                    style={{
                      padding: "10px 12px 3px 12px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Tooltip
                      title={t("initiatives.back_to_list")}
                      placement="top"
                    >
                      <Button
                        justIcon
                        color="client"
                        className={classes.viewButtonArrow}
                        onClick={toggleLayout}
                        disabled={screenBusy || initiativesBusy}
                      >
                        <ArrowBack className={classes.cardButtonIcon} />
                      </Button>
                    </Tooltip>
                  </div>
                  <div
                    className={classes.miniWrapper}
                    style={{ marginTop: "-5px" }}
                  >
                    {(() => {
                      const buckets = makeStageBuckets(
                        getSortedFilteredInitiativesList(),
                      );

                      return stages.map((stage) => {
                        const items = buckets[stage];
                        if (!items.length) return null;

                        const folded = collapsedStages.has(stage);
                        return (
                          <div key={stage} style={{ marginBottom: "8px" }}>
                            {/* Stage header – accordion style */}
                            <div
                              className={classes.miniStageHeaderAccordion}
                              onClick={() => toggleStage(stage)}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  flex: 1,
                                }}
                              >
                                <div
                                  className={classes.miniStageTitleAccordion}
                                >
                                  {t(`initiatives.stages.${stage}`)}
                                </div>
                              </div>
                              <span className={classes.miniStageCountAccordion}>
                                {items.length}
                              </span>
                              <Icon
                                fontSize="small"
                                className={classes.miniStageExpandIcon}
                              >
                                {folded ? "chevron_right" : "expand_more"}
                              </Icon>
                            </div>

                            {/* Bucket items */}
                            <Collapse in={!folded} timeout="auto" unmountOnExit>
                              <div
                                style={{
                                  paddingTop: "2px",
                                  paddingLeft: "0px",
                                }}
                              >
                                {items.map((it) => (
                                  <div
                                    key={it.id}
                                    className={cx({
                                      [classes.miniItemAccordion]: true,
                                      [classes.miniItemSelectedAccordion]:
                                        it.id === selectedInitiative,
                                    })}
                                    style={{
                                      borderLeft: `2px solid ${statusToColor(stage === "opportunity" ? null : it.displayStatus)}`,
                                    }}
                                    onClick={() => handleInitiativeClick(it.id)}
                                  >
                                    <div
                                      // variant="body2"
                                      className={
                                        classes.ellipsisTextMiniAccordion
                                      }
                                    >
                                      {it.initiative}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </Collapse>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </CardBody>
              </Card>
            </div>
          </GridItem>
        )}
        {/* Details, Execution, and Charts Section - All in 9-column space */}
        <GridItem
          xs={12}
          md={layoutMode === "expanded" ? false : 9}
          className={cx({
            [classes.invisible]: !selectedInitiative,
          })}
          style={{
            transition: "flex-basis .3s ease-in-out",
            marginTop: "-23px",
          }}
        >
          <GridContainer style={{ paddingBottom: "20px" }}>
            {/* Details Section - Top Left */}
            <GridItem xs={12} md={6}>
              <div className={classes.cardsFullHeight} ref={detailsPanelRef}>
                <div className={classes.cardsWrapper}>
                  <InitiativeDetailsCard
                    selectedInitiative={selectedInitiative}
                    initiativeForm={initiativeForm}
                    setInitiativeFormValue={setInitiativeFormValue}
                    initiativesBusy={initiativesBusy}
                    screenBusy={screenBusy}
                    stages={stages}
                    pnlStructure={pnlStructure}
                    tags={tags}
                    initiatives={initiatives}
                    checkAndSaveInitiative={checkAndSaveInitiative}
                    updateInitData={updateInitData}
                    handlers={handlers}
                    metaLoaded={metaLoaded}
                    filtersListTranslated={filtersListTranslated}
                    filtersActiveList={filtersActiveList}
                    filtersSequence={filtersSequence}
                    filterMenuClickHandler={filterMenuClickHandler}
                    currentScopeFilters={currentScopeFilters}
                    hasNoScopeFilters={hasNoScopeFilters}
                    selectedTagsFilters={selectedTagsFilters}
                    shortDescriptionInput={shortDescriptionInput}
                    initLongDescriptionRef={initLongDescriptionRef}
                  />
                </div>
              </div>
            </GridItem>

            {/* Execution Section - Top Right */}
            <GridItem xs={12} md={6}>
              <div
                className={classes.cardsFullHeight}
                style={{
                  height: detailsPanelHeight
                    ? `${detailsPanelHeight}px`
                    : "auto",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <InitiativeExecutionCard
                  selectedInitiative={selectedInitiative}
                  initiativeForm={initiativeForm}
                  setInitiativeFormValue={setInitiativeFormValue}
                  initiativesBusy={initiativesBusy}
                  screenBusy={screenBusy}
                  stages={stages}
                  initiatives={initiatives}
                  handlers={handlers}
                  clientColor={clientColor}
                  t={t}
                  validateMandatoryFieldsForStageChange={
                    validateMandatoryFieldsForStageChange
                  }
                  notify={notify}
                />
              </div>
            </GridItem>

            {/* Charts Section - Bottom Row (Full Width) */}
            {(monthlySalesChartData.datasets.length > 0 ||
              serveMarginMonthlyChartData.datasets.length > 0) && (
              <GridItem xs={12} md={12}>
                <div className={classes.cardsFullHeight}>
                  {/* New Card Above Graphs - Same height as first initiative details card */}
                  <Card
                    style={{
                      marginTop: "0",
                      marginBottom: "10px",
                      height: "32px",
                      backgroundImage: `linear-gradient(to right, ${blendColors(clientColor, grayColor[6], 0.5)} , ${clientColor})`,
                    }}
                  >
                    <CardHeader
                      style={{
                        padding: "0 6px 10px 12px",
                        position: "relative",
                      }}
                    >
                      <GridContainer>
                        <GridItem>
                          <p
                            className={classes.cardCategory}
                            style={{
                              fontWeight: "400",
                              textTransform: "uppercase",
                              paddingRight: "10px",
                              color: clientTextColor,
                            }}
                          >
                            {t("initiatives.results_within_scope")}
                          </p>
                        </GridItem>
                        <GridItem xs={true}></GridItem>
                      </GridContainer>
                    </CardHeader>
                  </Card>

                  {/* Charts Container - Side by Side */}
                  <GridContainer>
                    {/* First Chart - Monthly Sales */}
                    {monthlySalesChartData.datasets.length > 0 && (
                      <GridItem xs={12} md={6}>
                        <Card
                          style={{
                            backgroundColor: "white",
                            marginTop: "0px",
                            marginBottom: "0",
                            position: "relative",
                          }}
                        >
                          <div style={{ padding: "15px 15px 0 5px" }}>
                            <MultiLineChart
                              datasets={monthlySalesChartData.datasets}
                              labels={monthlySalesChartData.labels}
                              title={(() => {
                                const baseTitle =
                                  chartNames.sales_chart_name ||
                                  t(
                                    "overview.monthly_sales_within_scope",
                                  ).replace(" (M)", "");
                                const orderOfMagnitude =
                                  calculateOrderOfMagnitude(
                                    monthlySalesChartData,
                                  );
                                const oomSuffix = orderOfMagnitude
                                  ? ` ${t(`oom.${orderOfMagnitude}`)}`
                                  : "";
                                return selectedCurrency
                                  ? `${baseTitle} (${selectedCurrency}${oomSuffix})`
                                  : `${baseTitle}${oomSuffix}`;
                              })()}
                              height={260}
                            />
                          </div>
                          {showChartOverlay &&
                            monthlySalesChartData.datasets.length > 0 && (
                              <ChartOverlay />
                            )}
                        </Card>
                      </GridItem>
                    )}

                    {/* Second Chart - Serve Margin Monthly */}
                    {serveMarginMonthlyChartData.datasets.length > 0 && (
                      <GridItem xs={12} md={6}>
                        <Card
                          style={{
                            backgroundColor: "white",
                            marginTop: "0px",
                            marginBottom: "0",
                            position: "relative",
                          }}
                        >
                          <div style={{ padding: "15px 15px 0 5px" }}>
                            <MultiLineChart
                              datasets={serveMarginMonthlyChartData.datasets}
                              labels={serveMarginMonthlyChartData.labels}
                              title={
                                chartNames.serve_margin_chart_name ||
                                t("overview.serve_margin_monthly")
                              }
                              height={260}
                              isPercentage={true}
                            />
                          </div>
                          {showChartOverlay &&
                            serveMarginMonthlyChartData.datasets.length > 0 && (
                              <ChartOverlay />
                            )}
                        </Card>
                      </GridItem>
                    )}
                  </GridContainer>
                </div>
              </GridItem>
            )}
          </GridContainer>
        </GridItem>
      </GridContainer>
      <FullPageLoader isVisible={screenBusy} />
      {filtersDialog}
      <NewFilterDialog
        open={newFilterModalOpen}
        onClose={() => setNewFilterModalOpen(false)}
        tempNewFilters={tempNewFilters}
        setTempNewFilters={setTempNewFilters}
        newFilters={newFilters}
        setNewFilters={setNewFilters}
        stages={stages}
        tags={tags}
        leaders={getUniqueLeaders()}
        classes={classes}
        t={t}
      />
      <TagManagementDialog
        tagManagementModalOpen={tagManagementModalOpen}
        setTagManagementModalOpen={setTagManagementModalOpen}
        confirmDeleteTag={confirmDeleteTag}
        setConfirmDeleteTag={setConfirmDeleteTag}
        tags={tags}
        setTags={setTags}
        tagFilter={tagFilter}
        setTagFilter={setTagFilter}
        isAddingTag={isAddingTag}
        setIsAddingTag={setIsAddingTag}
        newTagName={newTagName}
        setNewTagName={setNewTagName}
        editingTag={editingTag}
        setEditingTag={setEditingTag}
        tagActionLoading={tagActionLoading}
        userDetails={userDetails}
        handleSaveNewTag={handleSaveNewTag}
        handleSaveEditedTag={handleSaveEditedTag}
        handleDeleteTag={handleDeleteTag}
        handleCancelEditing={handleCancelEditing}
        notify={notify}
        classes={classes}
        t={t}
      />
      {selectedInitiative && (
        <AddUpdateDialog
          addUpdateModalOpen={addUpdateModalOpen}
          setAddUpdateModalOpen={setAddUpdateModalOpen}
          initiatives={initiatives}
          selectedInitiative={selectedInitiative}
          initiativesBusy={initiativesBusy}
          saveAddUpdate={saveAddUpdate}
          classes={classes}
          t={t}
          clientColor={clientColor}
        />
      )}

      <DeleteInitiativeDialog
        open={deleteInitiativeModalOpen}
        setOpen={setDeleteInitiativeModalOpen}
        initiativeName={
          selectedInitiative !== "new" &&
          selectedInitiative !== null &&
          initiatives[selectedInitiative]
            ? initiatives[selectedInitiative].short_description ||
              initiatives[selectedInitiative].description ||
              t("initiatives.initiatives_lower")
            : t("initiatives.initiatives_lower")
        }
        onConfirm={() => {
          setIsDeletingInitiative(true);
          handleDeleteInitiative();
        }}
        isDeleting={isDeletingInitiative}
      />
    </div>
  );
}
